// Form badges — types + pure logic (Phase 3).
//
// Recent Form and Home/Away Form both render a strip of W/D/L badges per team.
// The D3 enter/update/exit that animates the badges lives in the component
// (React owns the container, D3 owns the badges); everything decidable without a
// DOM lives here, pure and unit-tested: which result a match was, its colour,
// its tooltip, and which dataset/window to read.

/** One match in a team's form history (legacy `recentForm`/`homeForm`/`awayForm`). */
export interface FormMatch {
  home: string;
  away: string;
  /** Full-time score, en-dash separated, e.g. "2–1". */
  score: string;
  /** Half-time score, same format. */
  ht: string;
  date: string;
}

/** Form history keyed by full team name. */
export type FormData = Record<string, FormMatch[]>;

/** Result letter for `team` in `match`: Win / Draw / Loss. */
export type FormResult = "W" | "D" | "L";

/** Which dataset a strip reads. */
export type FormVenue = "recent" | "home" | "away";

/** Result of a match from `team`'s perspective (score is "mine–theirs" by venue). */
export function matchResult(match: FormMatch, team: string): FormResult {
  const goals = match.score.split("–").map(Number);
  const isHome = match.home === team;
  const mine = isHome ? goals[0]! : goals[1]!;
  const theirs = isHome ? goals[1]! : goals[0]!;
  return mine > theirs ? "W" : mine < theirs ? "L" : "D";
}

/** Badge colour by result: win green, loss red, draw grey. */
export function formResultColor(r: FormResult): string {
  return r === "W" ? "#2ecc71" : r === "L" ? "#e74c3c" : "#888";
}

/** Badge tooltip text (home v away, score, date) — order preserved. */
export function formTooltip(m: FormMatch): string {
  return `${m.home} v ${m.away}\n${m.score}\n${m.date}`;
}

/** Venue a strip reads: Home/Away Form → home unless "Away"; Recent Form → recent. */
export function formVenue(isHomeAway: boolean, ha: string | null | undefined): FormVenue {
  if (!isHomeAway) return "recent";
  return ha === "Away" ? "away" : "home";
}

/** How many recent matches the window shows ("10" → 10, else 5). */
export function formWindow(timeline: string | null | undefined): number {
  return timeline === "10" ? 10 : 5;
}

/** The last `n` matches (most recent), matching the legacy `slice(-n)`. */
export function selectForm(matches: readonly FormMatch[], n: number): FormMatch[] {
  return matches.slice(-n);
}
